import { generateBoard, TERRAIN_RESOURCE, type BoardLayout } from './board';
import {
  COSTS,
  RESOURCES,
  emptyResources,
  hasResources,
  isResource,
  isResources,
  res,
  totalResources,
  transfer,
  type Resource,
  type Resources,
} from './resources';
import { mulberry32, rngNext, shuffle } from './rng';
import {
  DISCARD_THRESHOLD,
  MIN_LARGEST_ARMY,
  PIECES,
  bankRate,
  bankSizeFor,
  canAfford,
  canPlaceCity,
  canPlaceRoad,
  canPlaceSettlement,
  devDeckFor,
  houseRules,
  isHex,
  isIndex,
  knightsPlayed,
  legalRoadEdges,
  longestRoadHolder,
  playableDevCardIndex,
  productionFor,
  publicVictoryPoints,
  robberActive,
  setupOrder,
  specialBuildOn,
  stealCandidates,
  tradeIsWellFormed,
  victoryPoints,
} from './rules';
import { topologyOf } from './topology';
import type { Action, FirstRollRound, GameEvent, GameState, GameStats, HouseRules, PlayerState, ProgressCard, ReturnPhase, TradeResponse } from './types';

export interface CreateGameOptions {
  playerCount: number;
  seed: number;
  layout?: BoardLayout;
  victoryPointsToWin?: number;
  rules?: Partial<HouseRules>;
  /** Middle-row width of the island, or 'auto' to size it by player count. */
  mapSize?: number | 'auto';
  /** Who goes first: a seat, or 'roll' to roll the dice for it. Defaults to seat 0. */
  firstPlayer?: number | 'roll';
}

/**
 * Everyone rolls two dice; the highest goes first, and ties roll again among
 * themselves. Uses its own stream from the seed so a seed's board and dice
 * don't change with who won the roll.
 */
function rollForFirst(seed: number, players: number): { first: number; rounds: FirstRollRound[] } {
  const rng = mulberry32((seed ^ 0x5f3759df) >>> 0);
  const die = () => 1 + Math.floor(rng() * 6);
  let contenders = Array.from({ length: players }, (_, i) => i);
  const rounds: FirstRollRound[] = [];
  for (;;) {
    const round = contenders.map((player) => ({ player, dice: [die(), die()] as [number, number] }));
    rounds.push(round);
    const top = Math.max(...round.map((r) => r.dice[0] + r.dice[1]));
    contenders = round.filter((r) => r.dice[0] + r.dice[1] === top).map((r) => r.player);
    if (contenders.length === 1) return { first: contenders[0]!, rounds };
  }
}

/** The roll for first as an event, for the log of a game that just started. Empty if nobody rolled. */
export function firstRollEvents(state: GameState): GameEvent[] {
  return state.firstRoll ? [{ type: 'firstRoll', rounds: state.firstRoll, first: state.firstPlayer ?? 0 }] : [];
}

/** No upper limit: the board, bank, and deck grow with the table (see landmassFor, bankSizeFor, devDeckFor). */
export const MIN_PLAYERS = 2;
export const MIN_VICTORY_POINTS = 3;
export const MAX_VICTORY_POINTS = 50;

export function createGame(opts: CreateGameOptions): GameState {
  const { playerCount, seed } = opts;
  if (!Number.isInteger(playerCount) || playerCount < MIN_PLAYERS) {
    throw new Error(`playerCount must be at least ${MIN_PLAYERS}`);
  }
  const layout = opts.layout ?? 'random';
  const rules = houseRules({ config: { rules: opts.rules ?? {} } });
  const rng = mulberry32(seed);
  const vp = opts.victoryPointsToWin ?? 10;
  if (!Number.isInteger(vp) || vp < MIN_VICTORY_POINTS || vp > MAX_VICTORY_POINTS) {
    throw new Error(`victoryPointsToWin must be ${MIN_VICTORY_POINTS}–${MAX_VICTORY_POINTS}`);
  }
  const board = generateBoard(layout, rng, {
    playerCount,
    ...(typeof opts.mapSize === 'number' ? { mapSize: opts.mapSize } : {}),
    islands: rules.islands,
    scrambleNumbers: rules.scrambleNumbers,
  });
  const t = topologyOf({ board });
  const devDeck = shuffle(rng, devDeckFor(playerCount));
  const players: PlayerState[] = Array.from({ length: playerCount }, () => ({
    resources: emptyResources(),
    devCards: [],
    playedDevCards: [],
    playedDevCardThisTurn: false,
    roadsLeft: PIECES.roads,
    settlementsLeft: PIECES.settlements,
    citiesLeft: PIECES.cities,
  }));
  const bank = emptyResources();
  for (const r of RESOURCES) bank[r] = bankSizeFor(playerCount);
  const rolled = opts.firstPlayer === 'roll' ? rollForFirst(seed, playerCount) : null;
  const first = rolled ? rolled.first : isIndex(opts.firstPlayer, playerCount) ? opts.firstPlayer : 0;

  return {
    config: { playerCount, layout, victoryPointsToWin: vp, rules },
    rng: Math.floor(rng() * 2 ** 32) >>> 0,
    board,
    robber: board.tiles.findIndex((t) => t.terrain === 'desert'),
    bank,
    devDeck,
    players,
    buildings: Array(t.vertices.length).fill(null),
    roads: Array(t.edges.length).fill(null),
    phase: { kind: 'setup', index: 0, step: 'settlement', lastSettlement: null },
    turn: 0,
    currentPlayer: first,
    lastRoll: null,
    longestRoad: null,
    largestArmy: null,
    trade: null,
    nextTradeId: 1,
    offersThisTurn: 0,
    stats: newStats(playerCount),
    firstPlayer: first,
    ...(rolled ? { firstRoll: rolled.rounds } : {}),
  };
}

function newStats(players: number): GameStats {
  const zeros = () => Array<number>(players).fill(0);
  return {
    rolls: Array<number>(13).fill(0),
    produced: zeros(),
    blocked: zeros(),
    stolen: zeros(),
    lost: zeros(),
    discarded: zeros(),
    bankTrades: zeros(),
    playerTrades: zeros(),
    points: [],
  };
}

export type ActionResult = { ok: true; state: GameState; events: GameEvent[] } | { ok: false; error: string };

/** Pure reducer: returns a new state (the input is never mutated) or an error. */
export function applyAction(prev: GameState, player: number, action: Action): ActionResult {
  const error = validate(prev, player, action);
  if (error !== null) return { ok: false, error };
  const state = structuredClone(prev);
  const events: GameEvent[] = [];
  execute(state, player, action, events);
  checkVictory(state, events);
  return { ok: true, state, events };
}

// ---------------------------------------------------------------------------
// Validation (read-only). Returns an error message, or null if the action is legal.

export function validate(state: GameState, player: number, action: Action): string | null {
  if (!isIndex(player, state.config.playerCount)) return 'unknown player';
  const phase = state.phase;
  if (phase.kind === 'gameOver') return 'the game is over';
  const isCurrent = player === state.currentPlayer;
  const p = state.players[player]!;

  switch (action.type) {
    case 'buildSettlement': {
      if (!isCurrent) return 'not your turn';
      if (phase.kind === 'setup') {
        if (phase.step !== 'settlement') return 'place a road next';
        return canPlaceSettlement(state, player, action.vertex, true) ? null : 'cannot build a settlement there';
      }
      if (!buildPhase(phase)) return 'you cannot build right now';
      if (p.settlementsLeft <= 0) return 'no settlements left';
      if (!canAfford(state, player, COSTS.settlement)) return 'not enough resources';
      return canPlaceSettlement(state, player, action.vertex, false) ? null : 'cannot build a settlement there';
    }

    case 'buildRoad': {
      if (!isCurrent) return 'not your turn';
      if (phase.kind === 'setup') {
        if (phase.step !== 'road') return 'place a settlement first';
        return canPlaceRoad(state, player, action.edge, phase.lastSettlement!) ? null : 'road must touch your new settlement';
      }
      if (p.roadsLeft <= 0) return 'no roads left';
      if (phase.kind === 'roadBuilding') {
        return canPlaceRoad(state, player, action.edge) ? null : 'cannot build a road there';
      }
      if (!buildPhase(phase)) return 'you cannot build right now';
      if (!canAfford(state, player, COSTS.road)) return 'not enough resources';
      return canPlaceRoad(state, player, action.edge) ? null : 'cannot build a road there';
    }

    case 'buildCity': {
      if (!isCurrent) return 'not your turn';
      if (!buildPhase(phase)) return 'you cannot build right now';
      if (p.citiesLeft <= 0) return 'no cities left';
      if (!canAfford(state, player, COSTS.city)) return 'not enough resources';
      return canPlaceCity(state, player, action.vertex) ? null : 'you need your own settlement there';
    }

    case 'rollDice':
      if (!isCurrent) return 'not your turn';
      return phase.kind === 'preRoll' ? null : 'you cannot roll now';

    case 'discard': {
      if (phase.kind !== 'discard') return 'nobody needs to discard';
      const owed = phase.owed[player]!;
      if (owed === 0) return 'you do not need to discard';
      if (!isResources(action.resources)) return 'invalid resources';
      if (totalResources(action.resources) !== owed) return `discard exactly ${owed}`;
      return hasResources(p.resources, action.resources) ? null : 'you do not have those cards';
    }

    case 'moveRobber':
      if (!isCurrent) return 'not your turn';
      if (phase.kind !== 'moveRobber') return 'you cannot move the robber now';
      if (!isHex(topologyOf(state), action.hex)) return 'invalid hex';
      return action.hex === state.robber ? 'the robber must move to a different hex' : null;

    case 'steal':
      if (!isCurrent) return 'not your turn';
      if (phase.kind !== 'steal') return 'you cannot steal now';
      return phase.candidates.includes(action.victim) ? null : 'you cannot steal from that player';

    case 'buyDevCard':
      if (!isCurrent) return 'not your turn';
      if (!buildPhase(phase)) return 'you cannot buy now';
      if (state.devDeck.length === 0) return 'no development cards left';
      return canAfford(state, player, COSTS.devCard) ? null : 'not enough resources';

    case 'playKnight':
    case 'playRoadBuilding':
    case 'playYearOfPlenty':
    case 'playMonopoly': {
      if (!isCurrent) return 'not your turn';
      if (phase.kind !== 'preRoll' && phase.kind !== 'main') return 'you cannot play a card now';
      if (p.playedDevCardThisTurn) return 'you already played a development card this turn';
      if (playableDevCardIndex(state, player, cardFor(action.type)) < 0) return 'you have no playable card of that type';
      if (action.type === 'playKnight' && !robberActive(state)) return 'the robber is not in play yet';
      if (action.type === 'playRoadBuilding') {
        if (p.roadsLeft <= 0) return 'no roads left';
        if (legalRoadEdges(state, player).length === 0) return 'nowhere to build a road';
      }
      if (action.type === 'playYearOfPlenty') {
        const picks = action.resources;
        if (!Array.isArray(picks) || picks.length !== 2 || !picks.every(isResource)) return 'choose two resources';
        const need = res();
        for (const r of picks) need[r]++;
        if (!hasResources(state.bank, need)) return 'the bank does not have those';
      }
      if (action.type === 'playMonopoly' && !isResource(action.resource)) return 'choose a resource';
      return null;
    }

    case 'bankTrade': {
      if (!isCurrent) return 'not your turn';
      if (phase.kind !== 'main') return 'you cannot trade now';
      if (!isResource(action.give) || !isResource(action.receive) || action.give === action.receive) {
        return 'invalid trade';
      }
      if (p.resources[action.give] < bankRate(state, player, action.give)) return 'not enough resources';
      return state.bank[action.receive] > 0 ? null : 'the bank is out of that resource';
    }

    case 'offerTrade':
      if (!isCurrent) return 'only the current player can offer trades';
      if (phase.kind !== 'main') return 'you cannot trade now';
      if (!isResources(action.give) || !isResources(action.want)) return 'invalid resources';
      if (!tradeIsWellFormed(action.give, action.want)) return 'both sides must give something different';
      return hasResources(p.resources, action.give) ? null : 'you do not have those cards';

    case 'respondTrade': {
      const trade = state.trade;
      if (phase.kind !== 'main' || !trade) return 'there is no open trade';
      if (player === trade.from) return 'you cannot respond to your own offer';
      if (typeof action.accept !== 'boolean') return 'invalid response';
      if (action.accept && !hasResources(p.resources, trade.want)) return 'you do not have those cards';
      return null;
    }

    case 'counterTrade': {
      const trade = state.trade;
      if (phase.kind !== 'main' || !trade) return 'there is no open trade';
      if (player === trade.from) return 'you cannot counter your own offer';
      if (!isResources(action.give) || !isResources(action.want)) return 'invalid resources';
      if (!tradeIsWellFormed(action.give, action.want)) return 'both sides must give something different';
      return hasResources(p.resources, action.give) ? null : 'you do not have those cards';
    }

    case 'confirmTrade': {
      const trade = state.trade;
      if (phase.kind !== 'main' || !trade) return 'there is no open trade';
      if (player !== trade.from) return 'only the proposer can confirm';
      if (!isIndex(action.with, state.config.playerCount) || action.with === player) return 'invalid partner';
      const terms = tradeTerms(trade.give, trade.want, trade.responses[action.with] ?? null);
      if (!terms) return 'that player has not accepted';
      if (!hasResources(p.resources, terms.fromGives)) return 'you no longer have those cards';
      if (!hasResources(state.players[action.with]!.resources, terms.toGives)) return 'they no longer have those cards';
      return null;
    }

    case 'cancelTrade':
      if (!state.trade || state.trade.from !== player) return 'you have no open offer';
      return null;

    case 'requestBuild':
      if (!specialBuildOn(state)) return 'building between turns is off';
      if (isCurrent) return "it's your turn";
      if (phase.kind === 'setup' || phase.kind === 'specialBuild') return 'you can ask once the game is under way';
      return typeof action.on === 'boolean' ? null : 'invalid request';

    case 'endTurn':
      if (!isCurrent) return 'not your turn';
      return buildPhase(phase) ? null : 'you cannot end your turn now';

    default:
      return 'unknown action';
  }
}

function cardFor(type: 'playKnight' | 'playRoadBuilding' | 'playYearOfPlenty' | 'playMonopoly'): ProgressCard {
  switch (type) {
    case 'playKnight':
      return 'knight';
    case 'playRoadBuilding':
      return 'roadBuilding';
    case 'playYearOfPlenty':
      return 'yearOfPlenty';
    case 'playMonopoly':
      return 'monopoly';
  }
}

function tradeTerms(
  give: Resources,
  want: Resources,
  response: TradeResponse | null,
): { fromGives: Resources; toGives: Resources } | null {
  if (response?.kind === 'accept') return { fromGives: give, toGives: want };
  if (response?.kind === 'counter') return { fromGives: response.want, toGives: response.give };
  return null;
}

// ---------------------------------------------------------------------------
// Execution. Only called after validate() passed; mutates the cloned state.

function random(state: GameState): number {
  const [value, next] = rngNext(state.rng);
  state.rng = next;
  return value;
}

function rollDie(state: GameState): number {
  return 1 + Math.floor(random(state) * 6);
}

function returnTo(state: GameState, phase: ReturnPhase): void {
  state.phase = { kind: phase };
}

function execute(state: GameState, player: number, action: Action, events: GameEvent[]): void {
  const p = state.players[player]!;
  const phase = state.phase;

  switch (action.type) {
    case 'buildSettlement': {
      const setup = phase.kind === 'setup';
      if (!setup) transfer(p.resources, state.bank, COSTS.settlement);
      state.buildings[action.vertex] = { owner: player, kind: 'settlement' };
      p.settlementsLeft--;
      events.push({ type: 'built', player, piece: 'settlement', at: action.vertex, free: setup });
      if (phase.kind === 'setup') {
        if (phase.index >= state.config.playerCount) grantSetupResources(state, player, action.vertex, events);
        state.phase = { ...phase, step: 'road', lastSettlement: action.vertex };
      }
      updateLongestRoad(state, events);
      return;
    }

    case 'buildRoad': {
      const free = phase.kind === 'setup' || phase.kind === 'roadBuilding';
      if (!free) transfer(p.resources, state.bank, COSTS.road);
      state.roads[action.edge] = player;
      p.roadsLeft--;
      events.push({ type: 'built', player, piece: 'road', at: action.edge, free });
      updateLongestRoad(state, events);
      if (phase.kind === 'setup') advanceSetup(state, phase.index, events);
      if (phase.kind === 'roadBuilding') {
        const remaining = phase.remaining - 1;
        if (remaining > 0 && p.roadsLeft > 0 && legalRoadEdges(state, player).length > 0) {
          state.phase = { ...phase, remaining };
        } else {
          returnTo(state, phase.returnTo);
        }
      }
      return;
    }

    case 'buildCity':
      transfer(p.resources, state.bank, COSTS.city);
      state.buildings[action.vertex] = { owner: player, kind: 'city' };
      p.citiesLeft--;
      p.settlementsLeft++;
      events.push({ type: 'built', player, piece: 'city', at: action.vertex, free: false });
      return;

    case 'rollDice': {
      let dice: [number, number] = [rollDie(state), rollDie(state)];
      // House rule: until the robber is in play a 7 is re-rolled. Nothing a roll
      // does can activate the robber, so this loop only ever ends on a non-7.
      const resting = !robberActive(state);
      while (resting && dice[0] + dice[1] === 7) {
        events.push({ type: 'sevenRerolled', player, dice });
        dice = [rollDie(state), rollDie(state)];
      }
      state.lastRoll = dice;
      events.push({ type: 'rolled', player, dice });
      const total = dice[0] + dice[1];
      if (state.stats) state.stats.rolls[total]!++;
      if (total === 7) {
        const owed = state.players.map((pl) => {
          const n = totalResources(pl.resources);
          return n > DISCARD_THRESHOLD ? Math.floor(n / 2) : 0;
        });
        if (owed.some((n) => n > 0)) {
          state.phase = { kind: 'discard', owed };
          events.push({ type: 'mustDiscard', owed });
        } else {
          state.phase = { kind: 'moveRobber', returnTo: 'main' };
        }
      } else {
        produce(state, total, events);
        state.phase = { kind: 'main' };
      }
      return;
    }

    case 'discard': {
      if (phase.kind !== 'discard') return;
      transfer(p.resources, state.bank, action.resources);
      const owed = phase.owed.slice();
      owed[player] = 0;
      events.push({ type: 'discarded', player, resources: { ...action.resources } });
      if (state.stats) state.stats.discarded[player]! += totalResources(action.resources);
      state.phase = owed.every((n) => n === 0) ? { kind: 'moveRobber', returnTo: 'main' } : { kind: 'discard', owed };
      return;
    }

    case 'moveRobber': {
      if (phase.kind !== 'moveRobber') return;
      state.robber = action.hex;
      events.push({ type: 'robberMoved', player, hex: action.hex });
      const candidates = stealCandidates(state, player, action.hex);
      if (candidates.length === 1) {
        stealFrom(state, player, candidates[0]!, events);
        returnTo(state, phase.returnTo);
      } else if (candidates.length > 1) {
        state.phase = { kind: 'steal', hex: action.hex, candidates, returnTo: phase.returnTo };
      } else {
        returnTo(state, phase.returnTo);
      }
      return;
    }

    case 'steal':
      if (phase.kind !== 'steal') return;
      stealFrom(state, player, action.victim, events);
      returnTo(state, phase.returnTo);
      return;

    case 'buyDevCard': {
      transfer(p.resources, state.bank, COSTS.devCard);
      const card = state.devDeck.pop()!;
      p.devCards.push({ type: card, boughtOnTurn: state.turn });
      events.push({ type: 'boughtDevCard', player, card });
      return;
    }

    case 'playKnight':
    case 'playRoadBuilding':
    case 'playYearOfPlenty':
    case 'playMonopoly': {
      if (phase.kind !== 'preRoll' && phase.kind !== 'main') return;
      const card = cardFor(action.type);
      p.devCards.splice(playableDevCardIndex(state, player, card), 1);
      p.playedDevCards.push(card);
      p.playedDevCardThisTurn = true;
      events.push({ type: 'playedDevCard', player, card });
      const back: ReturnPhase = phase.kind;

      // Knights and road building leave the main phase; an open offer can't
      // be answered from there, so withdraw it rather than leave it dangling.
      if ((action.type === 'playKnight' || action.type === 'playRoadBuilding') && state.trade) {
        events.push({ type: 'tradeCancelled', id: state.trade.id });
        state.trade = null;
      }

      if (action.type === 'playKnight') {
        updateLargestArmy(state, player, events);
        state.phase = { kind: 'moveRobber', returnTo: back };
      } else if (action.type === 'playRoadBuilding') {
        state.phase = { kind: 'roadBuilding', remaining: Math.min(2, p.roadsLeft), returnTo: back };
      } else if (action.type === 'playYearOfPlenty') {
        const gain = res();
        for (const r of action.resources) gain[r]++;
        transfer(state.bank, p.resources, gain);
        events.push({ type: 'yearOfPlenty', player, resources: [action.resources[0], action.resources[1]] });
      } else {
        let taken = 0;
        state.players.forEach((other, i) => {
          if (i === player) return;
          taken += other.resources[action.resource];
          if (state.stats) state.stats.lost[i]! += other.resources[action.resource];
          p.resources[action.resource] += other.resources[action.resource];
          other.resources[action.resource] = 0;
        });
        events.push({ type: 'monopoly', player, resource: action.resource, taken });
        if (state.stats) state.stats.stolen[player]! += taken;
      }
      return;
    }

    case 'bankTrade': {
      const rate = bankRate(state, player, action.give);
      transfer(p.resources, state.bank, res({ [action.give]: rate }));
      transfer(state.bank, p.resources, res({ [action.receive]: 1 }));
      events.push({ type: 'bankTrade', player, give: action.give, giveCount: rate, receive: action.receive });
      if (state.stats) state.stats.bankTrades[player]!++;
      return;
    }

    case 'offerTrade': {
      const trade = {
        id: state.nextTradeId++,
        from: player,
        give: { ...action.give },
        want: { ...action.want },
        responses: state.players.map(() => null),
      };
      state.trade = trade;
      state.offersThisTurn = (state.offersThisTurn ?? 0) + 1;
      events.push({ type: 'tradeOffered', trade: structuredClone(trade) });
      return;
    }

    case 'respondTrade':
    case 'counterTrade': {
      const response: TradeResponse =
        action.type === 'counterTrade'
          ? { kind: 'counter', give: { ...action.give }, want: { ...action.want } }
          : { kind: action.accept ? 'accept' : 'decline' };
      state.trade!.responses[player] = response;
      events.push({ type: 'tradeResponded', player, response: structuredClone(response) });
      return;
    }

    case 'confirmTrade': {
      const trade = state.trade!;
      const terms = tradeTerms(trade.give, trade.want, trade.responses[action.with] ?? null)!;
      const partner = state.players[action.with]!;
      transfer(p.resources, partner.resources, terms.fromGives);
      transfer(partner.resources, p.resources, terms.toGives);
      events.push({
        type: 'tradeCompleted',
        from: player,
        to: action.with,
        fromGave: { ...terms.fromGives },
        toGave: { ...terms.toGives },
      });
      if (state.stats) {
        state.stats.playerTrades[player]!++;
        state.stats.playerTrades[action.with]!++;
      }
      state.trade = null;
      return;
    }

    case 'cancelTrade':
      events.push({ type: 'tradeCancelled', id: state.trade!.id });
      state.trade = null;
      return;

    case 'requestBuild': {
      const requests = state.buildRequests ?? state.players.map(() => false);
      requests[player] = action.on;
      state.buildRequests = requests;
      events.push({ type: 'buildRequested', player, on: action.on });
      return;
    }

    case 'endTurn': {
      if (state.trade) events.push({ type: 'tradeCancelled', id: state.trade.id });
      state.trade = null;
      // Whoever asked builds now, one at a time in play order, before the next turn starts.
      const turnOf = phase.kind === 'specialBuild' ? phase.turnOf : state.currentPlayer;
      const queue = phase.kind === 'specialBuild' ? phase.queue : buildersAfter(state, turnOf);
      delete state.buildRequests;
      const builder = queue[0];
      if (builder !== undefined) {
        state.currentPlayer = builder;
        state.phase = { kind: 'specialBuild', turnOf, queue: queue.slice(1) };
        events.push({ type: 'specialBuild', player: builder });
        return;
      }
      startTurn(state, (turnOf + 1) % state.config.playerCount, events);
      return;
    }
  }
}

/** Main-phase building, plus building between turns (no trading or dev cards there). */
function buildPhase(phase: GameState['phase']): boolean {
  return phase.kind === 'main' || phase.kind === 'specialBuild';
}

/** Players who asked to build, in play order after `turnOf`. */
function buildersAfter(state: GameState, turnOf: number): number[] {
  if (!specialBuildOn(state) || !state.buildRequests) return [];
  const n = state.config.playerCount;
  return Array.from({ length: n - 1 }, (_, i) => (turnOf + 1 + i) % n).filter((p) => state.buildRequests![p]);
}

function startTurn(state: GameState, player: number, events: GameEvent[]): void {
  state.currentPlayer = player;
  state.turn++;
  state.lastRoll = null;
  state.phase = { kind: 'preRoll' };
  for (const p of state.players) p.playedDevCardThisTurn = false;
  state.offersThisTurn = 0;
  delete state.rollGains;
  state.stats?.points.push(state.players.map((_, i) => publicVictoryPoints(state, i)));
  events.push({ type: 'turnStarted', player, turn: state.turn });
}

function advanceSetup(state: GameState, index: number, events: GameEvent[]): void {
  const order = setupOrder(state.config.playerCount, state.firstPlayer ?? 0);
  const next = index + 1;
  if (next < order.length) {
    state.currentPlayer = order[next]!;
    state.phase = { kind: 'setup', index: next, step: 'settlement', lastSettlement: null };
  } else {
    startTurn(state, state.firstPlayer ?? 0, events);
  }
}

function grantSetupResources(state: GameState, player: number, vertex: number, events: GameEvent[]): void {
  const gain = res();
  for (const hex of topologyOf(state).vertices[vertex]!.hexes) {
    const r = TERRAIN_RESOURCE[state.board.tiles[hex]!.terrain];
    if (r && state.bank[r] - gain[r] > 0) gain[r]++;
  }
  transfer(state.bank, state.players[player]!.resources, gain);
  events.push({ type: 'setupResources', player, resources: gain });
}

/**
 * Pays out a roll. If the bank can't cover everyone owed a resource, nobody
 * gets it, unless only one player is owed it, in which case they get what's left.
 */
function produce(state: GameState, total: number, events: GameEvent[]): void {
  const owed = productionFor(state, total);
  const gains = state.players.map(() => res());
  for (const r of RESOURCES) {
    const sum = owed.reduce((n, o) => n + o[r], 0);
    if (sum === 0) continue;
    if (state.bank[r] >= sum) {
      owed.forEach((o, i) => (gains[i]![r] = o[r]));
      continue;
    }
    const owedPlayers = owed.flatMap((o, i) => (o[r] > 0 ? [i] : []));
    if (owedPlayers.length === 1) gains[owedPlayers[0]!]![r] = state.bank[r];
    events.push({ type: 'shortage', resource: r });
  }
  gains.forEach((g, i) => transfer(state.bank, state.players[i]!.resources, g));
  events.push({ type: 'produced', gains });
  state.rollGains = gains.map((g) => ({ ...g }));
  const stats = state.stats;
  if (!stats) return;
  gains.forEach((g, i) => (stats.produced[i]! += totalResources(g)));
  const robbed = state.board.tiles[state.robber];
  if (robbed?.token === total && TERRAIN_RESOURCE[robbed.terrain]) {
    for (const v of topologyOf(state).hexVertices[state.robber]!) {
      const b = state.buildings[v];
      if (b) stats.blocked[b.owner]! += b.kind === 'city' ? 2 : 1;
    }
  }
}

function stealFrom(state: GameState, thief: number, victim: number, events: GameEvent[]): void {
  const from = state.players[victim]!.resources;
  let pick = Math.floor(random(state) * totalResources(from));
  let taken: Resource = RESOURCES[0];
  for (const r of RESOURCES) {
    if (pick < from[r]) {
      taken = r;
      break;
    }
    pick -= from[r];
  }
  transfer(from, state.players[thief]!.resources, res({ [taken]: 1 }));
  events.push({ type: 'stole', thief, victim, resource: taken });
  if (state.stats) {
    state.stats.stolen[thief]!++;
    state.stats.lost[victim]!++;
  }
}

function updateLargestArmy(state: GameState, player: number, events: GameEvent[]): void {
  const knights = knightsPlayed(state.players[player]!);
  const holder = state.largestArmy;
  if (knights < MIN_LARGEST_ARMY || holder === player) return;
  if (holder === null || knights > knightsPlayed(state.players[holder]!)) {
    state.largestArmy = player;
    events.push({ type: 'largestArmy', player });
  }
}

function updateLongestRoad(state: GameState, events: GameEvent[]): void {
  const next = longestRoadHolder(state);
  if (next !== state.longestRoad) {
    state.longestRoad = next;
    events.push({ type: 'longestRoad', player: next });
  }
}

// Victory only counts on your own turn, so a player pushed to 10 on someone
// else's turn (e.g. inheriting Longest Road) wins when their turn starts.
function checkVictory(state: GameState, events: GameEvent[]): void {
  // Points scored while building between turns count when that player's own turn comes.
  if (state.phase.kind === 'gameOver' || state.phase.kind === 'setup' || state.phase.kind === 'specialBuild') return;
  const p = state.currentPlayer;
  if (victoryPoints(state, p) >= state.config.victoryPointsToWin) {
    if (state.trade) events.push({ type: 'tradeCancelled', id: state.trade.id });
    state.trade = null;
    state.phase = { kind: 'gameOver', winner: p, resume: state.phase };
    // The game's over, so every card counts in the final tally.
    state.stats?.points.push(state.players.map((_, i) => victoryPoints(state, i)));
    events.push({ type: 'gameOver', winner: p });
  }
}

/** Lowest target that keeps a finished game going: past the target, and past the current player's score so they don't win again on the spot. */
export function minContinueTarget(state: GameState): number {
  return Math.max(state.config.victoryPointsToWin, victoryPoints(state, state.currentPlayer)) + 1;
}

/**
 * After a win, raise the points needed and pick up where play stopped. Not an
 * Action: it's a table decision (the host's, online), not a move by a seat.
 */
export function continueGame(prev: GameState, target: number): ActionResult {
  if (prev.phase.kind !== 'gameOver') return { ok: false, error: 'the game is not over' };
  if (!Number.isInteger(target) || target < minContinueTarget(prev) || target > MAX_VICTORY_POINTS) {
    return { ok: false, error: `play to between ${minContinueTarget(prev)} and ${MAX_VICTORY_POINTS} points` };
  }
  const state = structuredClone(prev);
  // Games that ended before `resume` existed were always won mid-turn.
  state.phase = prev.phase.kind === 'gameOver' && prev.phase.resume ? structuredClone(prev.phase.resume) : { kind: 'main' };
  state.config.victoryPointsToWin = target;
  return { ok: true, state, events: [{ type: 'gameContinued', target }] };
}

// ---------------------------------------------------------------------------
// Legal action enumeration, built by filtering candidates through validate().

const YEAR_OF_PLENTY_PICKS: [Resource, Resource][] = RESOURCES.flatMap((a, i) =>
  RESOURCES.slice(i).map((b): [Resource, Resource] => [a, b]),
);

/**
 * Every concrete legal action for `player`. Open-ended actions are not
 * enumerated: `discard` (see discardOwed), `offerTrade`, and `counterTrade`.
 */
export function legalActions(state: GameState, player: number): Action[] {
  const kind = state.phase.kind;
  if (kind === 'gameOver' || !isIndex(player, state.config.playerCount)) return [];
  const candidates: Action[] = [];
  const isCurrent = player === state.currentPlayer;
  const t = topologyOf(state);

  if (isCurrent) {
    if (kind === 'setup' || kind === 'main') {
      for (let v = 0; v < t.vertices.length; v++) candidates.push({ type: 'buildSettlement', vertex: v });
    }
    if (kind === 'setup' || kind === 'main' || kind === 'roadBuilding') {
      for (let e = 0; e < t.edges.length; e++) candidates.push({ type: 'buildRoad', edge: e });
    }
    if (kind === 'main') {
      for (let v = 0; v < t.vertices.length; v++) candidates.push({ type: 'buildCity', vertex: v });
      candidates.push({ type: 'buyDevCard' }, { type: 'endTurn' }, { type: 'cancelTrade' });
      for (const give of RESOURCES) {
        for (const receive of RESOURCES) if (give !== receive) candidates.push({ type: 'bankTrade', give, receive });
      }
      for (let i = 0; i < state.config.playerCount; i++) candidates.push({ type: 'confirmTrade', with: i });
    }
    if (kind === 'preRoll') candidates.push({ type: 'rollDice' });
    if (kind === 'preRoll' || kind === 'main') {
      candidates.push({ type: 'playKnight' }, { type: 'playRoadBuilding' });
      for (const pick of YEAR_OF_PLENTY_PICKS) candidates.push({ type: 'playYearOfPlenty', resources: pick });
      for (const r of RESOURCES) candidates.push({ type: 'playMonopoly', resource: r });
    }
    if (kind === 'moveRobber') {
      for (let h = 0; h < t.hexes.length; h++) candidates.push({ type: 'moveRobber', hex: h });
    }
    if (state.phase.kind === 'steal') {
      for (const victim of state.phase.candidates) candidates.push({ type: 'steal', victim });
    }
    if (kind === 'specialBuild') {
      for (let v = 0; v < t.vertices.length; v++) candidates.push({ type: 'buildSettlement', vertex: v }, { type: 'buildCity', vertex: v });
      for (let e = 0; e < t.edges.length; e++) candidates.push({ type: 'buildRoad', edge: e });
      candidates.push({ type: 'buyDevCard' }, { type: 'endTurn' });
    }
  } else {
    if (kind === 'main' && state.trade) candidates.push({ type: 'respondTrade', accept: true }, { type: 'respondTrade', accept: false });
    candidates.push({ type: 'requestBuild', on: !state.buildRequests?.[player] });
  }

  return candidates.filter((a) => validate(state, player, a) === null);
}
